import React, { useCallback, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useInView } from 'react-intersection-observer';
import axios from 'axios';

import { logger } from 'packages';
import { useUrlLocalizer } from 'tradera-lang/translate';
import {
    defaultJsonRequestHeaders,
    handleError,
    API_ERRORS,
    reloadOnUnauthorized
} from 'tradera-utils/api';
import adsActions from 'tradera-state/ads/actions';
import itemActions from 'tradera-state/items/actions';
import uiActions from 'tradera-state/ui/actions';
import ItemCard from 'tradera-components/item-card/item-card';
import { lazyLoadSettings } from 'tradera-components/item-card/config';
import 'tradera-components/item-card/layout.scss';
import { ComponentsWithStitchAds } from 'tradera-apps/tradera/components/discover/components/search-results/items-with-stiched-ads';
import { touchWebClient } from 'tradera-utils/http';

const LOADING_STATES = {
    // can transition to is represented by '->'
    initializing: 'initializing', // -> loading
    loading: 'loading', // -> ready, end, error
    ready: 'ready', // -> loading
    end: 'end', // final state
    error: 'error' // final state
};

const GPT_PAGE_NAMES = {
    CRP: 'crp'
};

function Recommendations(props) {
    const {
        addItems,
        children,
        className,
        collapseIfEmpty,
        createRequestUrl,
        enableQuickView,
        errorFallback,
        gtmLabel,
        infiniteScrollEnabled,
        isLoggedIn,
        itemCardProps,
        items,
        loader,
        noMoreItemsMarker,
        onError,
        onReachedEnd,
        openQuickView,
        preferredCurrency,
        setAds,
        showAds,
        title,
        toggleWishlist
    } = props;

    const [loadingState, setLoadingState] = useState(
        LOADING_STATES.initializing
    );
    const [currentPage, setCurrentPage] = useState(0);
    const [itemIds, setItemIds] = useState([]);

    const { toLocalizedUrl } = useUrlLocalizer();

    const [loadingTriggerRef, loadingTriggerInView] = useInView({
        rootMargin: '0px 0px 800px 0px', // Increase to load earlier
        threshold: 1.0
    });

    const addItemIds = useCallback(
        itemIdsToAdd => {
            setItemIds([...itemIds, ...itemIdsToAdd]);
        },
        [itemIds, setItemIds]
    );

    // For cancelling request when unmounting component:
    const cancelToken = useRef();
    useEffect(() => {
        cancelToken.current = axios.CancelToken.source();
        return cancelToken.current.cancel;
    }, []);

    const loadAds = useCallback(
        items => {
            if (!showAds) return;

            return touchWebClient
                .get(
                    `/ads/get-gpt-data?gptPageName=${
                        GPT_PAGE_NAMES.CRP
                    }&categoryId=${items?.[0]?.categoryId ||
                        0}&itemsLength=${items?.length || 0}`
                )
                .then(({ data }) => {
                    setAds({ gpt: data });
                    return data;
                });
        },
        [setAds, showAds]
    );

    const getItems = useCallback(
        page => {
            const endpoint = toLocalizedUrl(createRequestUrl(page));
            axios
                .get(endpoint, {
                    cancelToken: cancelToken.current.token,
                    headers: defaultJsonRequestHeaders
                })
                .then(({ data }) => {
                    const itemsInResponse = data?.items || [];
                    if (page === 1) {
                        loadAds(itemsInResponse);
                    }
                    if (itemsInResponse.length === 0) {
                        if (itemIds.length === 0) {
                            setLoadingState(LOADING_STATES.error);
                            if (onError) onError();
                        } else {
                            setLoadingState(LOADING_STATES.end);
                            if (onReachedEnd) onReachedEnd(itemIds);
                        }
                    } else {
                        addItems(itemsInResponse);
                        addItemIds(itemsInResponse.map(x => x.itemId));
                        setLoadingState(LOADING_STATES.ready);
                    }
                    return itemsInResponse;
                })
                .catch(handleError())
                .catch(reloadOnUnauthorized)
                .catch(error => {
                    switch (error.message) {
                        case API_ERRORS.CANCELLED:
                            return;
                        case API_ERRORS.ABORTED:
                        case API_ERRORS.NETWORK:
                            break;
                        default:
                            logger(error);
                    }
                    if (itemIds.length === 0) {
                        setLoadingState(LOADING_STATES.error);
                        if (onError) onError();
                    } else {
                        setLoadingState(LOADING_STATES.end);
                    }
                });
        },
        [
            toLocalizedUrl,
            createRequestUrl,
            loadAds,
            itemIds,
            onError,
            onReachedEnd,
            addItems,
            addItemIds
        ]
    );

    useEffect(() => {
        if (
            loadingState === LOADING_STATES.initializing ||
            (loadingState === LOADING_STATES.ready && loadingTriggerInView)
        ) {
            setLoadingState(LOADING_STATES.loading);
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            return getItems(nextPage);
        } else {
            return;
        }
    }, [
        currentPage,
        getItems,
        infiniteScrollEnabled,
        loadingState,
        loadingTriggerInView
    ]);

    const handleQuickViewClick = useCallback(
        itemId => {
            openQuickView(itemId, itemIds, gtmLabel);
        },
        [gtmLabel, itemIds, openQuickView]
    );

    if (collapseIfEmpty && loadingState === LOADING_STATES.error) {
        return null;
    }

    const itemComponents = itemIds.map((itemId, index) => (
        <div key={index} className="item-card-container flex-shrink-0">
            <ItemCard
                isLoggedIn={isLoggedIn}
                item={items[itemId]}
                lazy={lazyLoadSettings}
                onAddToWishlist={toggleWishlist}
                onQuickView={enableQuickView && handleQuickViewClick}
                onRemoveFromWishlist={toggleWishlist}
                preferredCurrency={preferredCurrency}
                ready={!!items[itemId].itemUrl}
                {...itemCardProps}
            />
        </div>
    ));

    return (
        <div className={`product-recommendations ${className}`}>
            {title}
            <section
                className={`item-card-layout layout-grid d-flex ${
                    !infiniteScrollEnabled
                        ? 'flex-nowrap flex-md-wrap mx-lg-n1'
                        : 'flex-wrap infinite-scroll'
                } `}>
                {showAds ? (
                    <ComponentsWithStitchAds
                        adsEnabled={true}
                        pageIndex={1}
                        skipTopAd={true}
                        components={itemComponents}
                    />
                ) : (
                    itemComponents
                )}
            </section>
            {loadingState === LOADING_STATES.loading && loader}
            {loadingState === LOADING_STATES.error && errorFallback}
            {loadingState === LOADING_STATES.end && noMoreItemsMarker}
            {infiniteScrollEnabled &&
                loadingState !== LOADING_STATES.end &&
                loadingState !== LOADING_STATES.error && (
                    <div
                        ref={loadingTriggerRef}
                        style={{
                            height: '1px',
                            width: '1px',
                            margin: '1px',
                            display:
                                loadingState === LOADING_STATES.loading
                                    ? 'none'
                                    : 'block'
                        }}
                        data-recommendations-infinite-scroll-trigger=""
                    />
                )}
            {loadingState === LOADING_STATES.ready && children}
        </div>
    );
}

Recommendations.propTypes = {
    className: PropTypes.string,
    collapseIfEmpty: PropTypes.bool,
    createRequestUrl: PropTypes.func.isRequired,
    enableQuickView: PropTypes.bool,
    errorFallback: PropTypes.node,
    infiniteScrollEnabled: PropTypes.bool,
    loader: PropTypes.node.isRequired,
    noMoreItemsMarker: PropTypes.node,
    onError: PropTypes.func,
    onReachedEnd: PropTypes.func,
    openQuickView: PropTypes.func,
    title: PropTypes.element,
    showAds: PropTypes.bool
};

Recommendations.defaultProps = {
    className: '',
    collapseIfEmpty: true,
    enableQuickView: false,
    infiniteScrollEnabled: false,
    showAds: false
};

const mapStateToProps = state => ({
    items: state.items.cache,
    isLoggedIn: state.member.isLoggedIn,
    preferredCurrency: state.multiCurrency.preferredCurrency,
    enableQuickView: state.environment.featureSwitches['quick-view']
});

const mapDispatchToProps = dispatch => ({
    addItems: items => dispatch(itemActions.add(items)),
    openQuickView: (itemId, itemIds, openedFrom) =>
        dispatch(uiActions.openQuickView({ itemIds, itemId, openedFrom })),
    setAds: ads => dispatch(adsActions.setAds(ads)),
    toggleWishlist: itemId => dispatch(itemActions.wishlist(itemId))
});

export { Recommendations, LOADING_STATES };
export default connect(mapStateToProps, mapDispatchToProps)(Recommendations);
